import { FormHelperText } from "@mui/material";
import { Theme } from "@mui/material/styles";
import { memo, useEffect, useMemo, useState } from "react";
import PhoneInput, { CountryData, PhoneInputProps } from "react-phone-input-2";
import useStrings from "../../hooks/useStrings";
import Box from "../Box";
import TitleValue from "../TitleValue";

import { Mode } from "../../constants/enums";

import "./index.css";
import Helpers from "../../commons/helpers";

interface IProps extends PhoneInputProps {
    mode?: Mode;
    required?: boolean;
    disabled?: boolean;
    isFullRow?: boolean;
    defaultValue: string;
    errorMessage?: string;
    onChangeValue: (value: string, countryData: CountryData) => void;
    variant?: "standard" | "outlined";
}

const PhoneNumberInput = ({
    mode,
    disabled,
    required,
    isFullRow,
    defaultValue,
    errorMessage,
    onChangeValue,
    variant = "standard",
    ...rest
}: IProps) => {
    const strings = useStrings();
    const [value, setValue] = useState<string>("");

    useEffect(() => {
        setValue(defaultValue);
    }, [defaultValue]);

    const onChange = (newValue: any, countryData: CountryData) => {
        setValue(newValue);
        onChangeValue(newValue, countryData);
    };

    const text = useMemo(() => {
        return value ? `+${value}` : "";
    }, [value]);

    if (mode === Mode.View) return <TitleValue isFullRow={isFullRow} title={strings.Organization.PHONE_NUMBER} value={text} />;

    return (
        <Box sx={(theme) => container(theme, { variant, error: !Helpers.isNullOrEmpty(errorMessage) })}>
            <Box>
                <PhoneInput
                    {...rest}
                    value={value}
                    disabled={disabled}
                    onChange={(phone: any, countryData: CountryData) => onChange(phone, countryData)}
                    specialLabel={`${strings.Organization.PHONE_NUMBER} ${required ? "*" : ""}`}
                    defaultErrorMessage="true"
                    containerStyle={{
                        ...(errorMessage &&
                            variant !== "outlined" && {
                            borderBottom: `1px solid #F44335`,
                            "&:hover": {
                                borderBottom: `1px solid #F44335`,
                                boxShadow: `0 2px 2px -2px #F44335`,
                            },
                            "& .special-label": {
                                color: "#F44335",
                            },
                        }),
                    }}
                />
            </Box>
            <Box>
                <FormHelperText error sx={{ ml: "14px" }}>
                    {errorMessage}
                </FormHelperText>
            </Box>
        </Box>
    );
};

const container = (theme: Theme, ownerState: { variant?: "standard" | "outlined"; error: boolean }) => {
    const {
        palette: { error },
    } = theme;
    return {
        width: "100%",
        display: "flex",
        flexWrap: "wrap",
        flexDirection: "row",
        alignItems: "center",
        position: "relative",
        justifyContent: "center",
        "& .MuiBox-root:nth-child(1)": {
            width: "100%",
        },

        "& .MuiBox-root:nth-child(2)": {
            width: "100%",
            position: "absolute",
            bottom: -18,
        },

        "& .react-tel-input .special-label": {
            fontSize: "1rem",
            transform: "scale(0.75)",
            ...(ownerState?.error && {
                color: error.main,
            }),
        },

        ...(ownerState.variant === "outlined" && {
            "& .react-tel-input .form-control": {
                border: `1px solid #cacaca`,
                borderRadius: "6px",
                "&:hover": {
                    border: `1px solid #0003`,
                },
                "&:focus": {
                    border: `2px solid #1976d2`,
                    boxShadow: `0 2px 2px -2px #1976d2`,
                },
            },
        }),
    };
};

export default memo(PhoneNumberInput);
